import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { RefreshCcw, Search, Trash2 } from 'lucide-react'
import { apiClient } from '../services/api'
import { useTranslation } from '../contexts/TranslationContext'
import Button from '../components/ui/Button'
import PageHeader from '../components/ui/PageHeader'
import PageShell from '../components/ui/PageShell'
import { ui } from '../styles/ui'
import { formatDateTime } from '../utils/date'
import { formatEmptyValue } from '../utils/displayValue'

const QRCODE_PRODUCT_EXTRACTION_STATUS_STORAGE_KEY = 'queue_qrcode_product_extraction_status'

export default function QRCodeProductExtractionQueue() {
  const { t } = useTranslation()
  const [receipts, setReceipts] = useState([])
  const [loading, setLoading] = useState(false)
  const [search, setSearch] = useState('')
  const [status, setStatus] = useState(() => {
    if (typeof window === 'undefined') return 'pending'
    return window.localStorage.getItem(QRCODE_PRODUCT_EXTRACTION_STATUS_STORAGE_KEY) || 'pending'
  })
  const [selectedIds, setSelectedIds] = useState([])
  const [page, setPage] = useState(1)
  const [receiptImages, setReceiptImages] = useState({})
  const [zoomImage, setZoomImage] = useState(null)
  const [totalPages, setTotalPages] = useState(1)
  const [totalCount, setTotalCount] = useState(0)
  const [processingTotal, setProcessingTotal] = useState(0)
  const [processingItems, setProcessingItems] = useState([])
  const [queueLabel, setQueueLabel] = useState('')

  const loadReceipts = async (pageNumber = 1) => {
    try {
      setLoading(true)
      const params = { page: pageNumber }
      if (search) params.search = search
      if (status) params.status = status

      const response = await apiClient.get('/queue/qrcode-product-extraction', { params })
      setReceipts(response.data.data || [])
      setPage(response.data.current_page || 1)
      setTotalPages(response.data.last_page || 1)
      setTotalCount(response.data.queued_total ?? 0)
      setProcessingTotal(response.data.processing_total ?? 0)
      setProcessingItems(response.data.processing_items || [])
      setQueueLabel(response.data.queue_label || '')
    } catch (err) {
      console.error('Failed to load QR code product extraction queue', err)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadReceipts(1)
  }, [])

  useEffect(() => {
    window.localStorage.setItem(QRCODE_PRODUCT_EXTRACTION_STATUS_STORAGE_KEY, status)
    loadReceipts(1)
  }, [status])

  useEffect(() => {
    const interval = setInterval(() => {
      loadReceipts(page)
    }, 30000)

    return () => clearInterval(interval)
  }, [page, status, search])

  const retryReceipt = async (receiptId) => {
    try {
      await apiClient.post(`/queue/qrcode-product-extraction/${receiptId}/retry`)
      loadReceipts(page)
    } catch (err) {
      console.error('Failed to retry QR code product extraction', err)
    }
  }

  const handleSelectReceipt = (receiptId) => {
    setSelectedIds((prev) =>
      prev.includes(receiptId) ? prev.filter((id) => id !== receiptId) : [...prev, receiptId]
    )
  }

  const handleSelectAll = () => {
    if (selectedIds.length === receipts.length) {
      setSelectedIds([])
      return
    }

    setSelectedIds(receipts.map((receipt) => receipt.id))
  }

  const handleBulkDelete = async () => {
    if (!selectedIds.length) {
      return
    }

    if (!window.confirm(`Delete ${selectedIds.length} selected receipt(s)?`)) {
      return
    }

    try {
      await apiClient.delete('/queue/qrcode-product-extraction', { data: { ids: selectedIds } })
      setSelectedIds([])
      loadReceipts(1)
    } catch (err) {
      console.error('Failed to delete selected receipts from QR code queue', err)
    }
  }

  const arrayBufferToBase64 = (buffer) => {
    let binary = ''
    const bytes = new Uint8Array(buffer)
    for (let i = 0; i < bytes.byteLength; i++) {
      binary += String.fromCharCode(bytes[i])
    }
    return window.btoa(binary)
  }

  const fetchReceiptImage = async (receiptId) => {
    if (!receiptId || receiptImages[receiptId]) {
      return
    }

    try {
      const response = await apiClient.get(`/receipts/${receiptId}/image`, {
        responseType: 'arraybuffer',
      })

      const base64Data = arrayBufferToBase64(response.data)
      const contentType = response.headers['content-type'] || 'image/jpeg'
      setReceiptImages((prev) => ({
        ...prev,
        [receiptId]: `data:${contentType};base64,${base64Data}`,
      }))
    } catch (err) {
      console.error('Failed to fetch receipt image', receiptId, err)
    }
  }

  useEffect(() => {
    receipts.forEach((receipt) => {
      if (receipt?.id && !receiptImages[receipt.id]) {
        fetchReceiptImage(receipt.id)
      }
    })
  }, [receipts])

  const retryAllReceipts = async () => {
    try {
      await apiClient.post('/queue/qrcode-product-extraction/retry-all', {
        search,
        status,
      })
      loadReceipts(1)
    } catch (err) {
      console.error('Failed to retry all QR code product extraction receipts', err)
    }
  }

  return (
    <PageShell>
      <PageHeader
        title={t('QR Code Product Extraction Queue', 'QR Code Product Extraction Queue')}
        subtitle={t('Monitor receipts waiting for QR code product extraction from receipt URLs.', 'Monitor receipts waiting for QR code product extraction from receipt URLs.')}
        actions={
          <div className="flex flex-wrap items-center gap-3">
            {selectedIds.length > 0 && (
              <Button variant="danger" onClick={handleBulkDelete}>
                <Trash2 size={16} /> {t('Delete selected', 'Delete selected')}
              </Button>
            )}
            <Button variant="ghost" onClick={() => loadReceipts(1)}>
              <RefreshCcw size={16} className="mr-1" /> {t('Refresh', 'Refresh')}
            </Button>
            <span className={`${ui.badge} ${ui.badgeNeutral}`}>
              {t('Total queued', 'Total queued')}: {totalCount}
            </span>
            <span
              title={processingTotal ? `${t('Working on', 'Working on')} ${queueLabel}` : t('Idle', 'Idle')}
              className={`${ui.badge} ${processingTotal ? ui.badgeSuccess : ui.badgeNeutral}`}
            >
              {processingTotal ? `${t('Active', 'Active')}: ${processingTotal}` : t('Idle', 'Idle')}
            </span>
          </div>
        }
      />

      <div className="grid gap-4 md:grid-cols-3">
        <div className={`${ui.card} ${ui.cardBody}`}>
          <label className={`${ui.label} mb-2`}>{t('Search', 'Search')}</label>
          <div className="flex items-center gap-2">
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className={ui.input}
              placeholder={t('Receipt ID, file name, or QR code URL', 'Receipt ID, file name, or QR code URL')}
            />
            <Button type="button" onClick={() => loadReceipts(1)}>
              <Search size={16} />
            </Button>
          </div>
        </div>

        <div className={`${ui.card} ${ui.cardBody}`}>
          <label className={`${ui.label} mb-2`}>{t('Status', 'Status')}</label>
          <select
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            className={ui.select}
          >
            <option value="all">{t('All', 'All')}</option>
            <option value="pending">{t('Pending', 'Pending')}</option>
            <option value="processing">{t('Processing', 'Processing')}</option>
            <option value="completed">{t('Completed', 'Completed')}</option>
            <option value="failed">{t('Failed', 'Failed')}</option>
          </select>
        </div>
      </div>

      <div className={`${ui.card} ${ui.cardBody}`}>
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <p className="text-sm font-semibold text-foreground">{t('Current queue status', 'Current queue status')}</p>
            <p className={ui.subtitle}>
              {processingTotal > 0
                ? `${t('Processing', 'Processing')} ${processingTotal} ${t('receipt', 'receipt')}${processingTotal === 1 ? '' : 's'}`
                : t('No active extraction jobs at this time.', 'No active extraction jobs at this time.')}
            </p>
          </div>
          <span className={`${ui.badge} ${ui.badgeNeutral}`}>{queueLabel}</span>
        </div>
      </div>

      <div className={ui.card}>
        <div className={ui.cardBody}>
          <table className={ui.table}>
            <thead className={ui.tableHead}>
              <tr>
                <th className={ui.tableHeadCell}>{t('Select', 'Select')}</th>
                <th className={ui.tableHeadCell}>{t('Receipt', 'Receipt')}</th>
                <th className={ui.tableHeadCell}>{t('QR Link', 'QR Link')}</th>
                <th className={ui.tableHeadCell}>{t('Status', 'Status')}</th>
                <th className={ui.tableHeadCell}>{t('Updated', 'Updated')}</th>
                <th className={ui.tableHeadCell}>{t('Actions', 'Actions')}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-divider">
              {receipts.map((receipt) => (
                <tr key={receipt.id} className={selectedIds.includes(receipt.id) ? 'bg-blue-50' : 'bg-surface hover:bg-surface-elevated'}>
                  <td className={ui.tableCell}>
                    <input
                      type="checkbox"
                      checked={selectedIds.includes(receipt.id)}
                      onChange={() => handleSelectReceipt(receipt.id)}
                      className="h-4 w-4 rounded border-outline text-blue-600 focus:ring-blue-500"
                    />
                  </td>
                  <td className={ui.tableCell}>
                    <div className="font-semibold text-foreground">{receipt.file_name}</div>
                    <div className="text-xs text-muted-foreground">{t('ID', 'ID')} {receipt.id}</div>
                  </td>
                  <td className={ui.tableCellText}>
                    {formatEmptyValue(receipt.qr_code_link)}
                  </td>
                  <td className={ui.tableCell}>
                    <span className={`${ui.badge} ${receipt.qr_code_product_extraction_status === 'completed' ? ui.badgeSuccess : receipt.qr_code_product_extraction_status === 'failed' ? ui.badgeError : ui.badgeNeutral}`}>
                      {receipt.qr_code_product_extraction_status || t('pending', 'pending')}
                    </span>
                    {receipt.qr_code_product_extraction_error && (
                      <div className={`mt-2 text-xs ${ui.alertError}`}>{receipt.qr_code_product_extraction_error}</div>
                    )}
                  </td>
                  <td className={ui.tableCellText}>{receipt.updated_at ? formatDateTime(receipt.updated_at) : formatEmptyValue(null)}</td>
                  <td className={ui.tableCell}>
                    <div className="flex gap-1">
                      <Link to={`/admin/jobs/${receipt.id}`} className={ui.buttonSmGhost}>
                        {t('View', 'View')}
                      </Link>
                      <Button size="sm" type="button" onClick={() => retryReceipt(receipt.id)}>
                        {t('Retry', 'Retry')}
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className={`${ui.cardBody} mt-4`}>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <Button variant="ghost" type="button" onClick={handleSelectAll}>
                {selectedIds.length === receipts.length ? t('Unselect All', 'Unselect All') : t('Select All', 'Select All')}
              </Button>
              <Button variant="danger" type="button" onClick={handleBulkDelete} disabled={selectedIds.length === 0}>
                {t('Delete selected', 'Delete selected')}
              </Button>
            </div>

            <div className="flex items-center gap-2">
              <Button variant="ghost" type="button" onClick={() => loadReceipts(page)}>
                {t('Refresh', 'Refresh')}
              </Button>
              <Button type="button" onClick={retryAllReceipts}>
                {t('Retry all pending', 'Retry all pending')}
              </Button>
            </div>
          </div>

          <div className="mt-4 flex flex-wrap items-center justify-between gap-3 text-sm text-muted-foreground">
            <div>{t('Page', 'Page')} {page} {t('of', 'of')} {totalPages}</div>
            <div>{receipts.length} {t('receipt', 'receipt')}{receipts.length === 1 ? '' : 's'} {t('shown', 'shown')}</div>
          </div>
        </div>
      </div>
    </PageShell>
  )
}

